// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Video from "@mcmc/App/Components/YoutubeVideo";
import loadYoutubePlaylistData from "@mcmc/App/Handlers/Media/LoadYoutubePlaylist";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getMediaLoadingState, getYoutubePlaylistData } from "@mcmc/App/Store/Selectors/MediaSelectors";
import { getSettingsCollection } from "@mcmc/App/Store/Selectors/SettingsSelectors";
import { createSectionTitleWidget } from "@mcmc/App/Widgets/SectionTitleWidget/SectionTitleWidget";
import Carousel from "@mcmc/Overrides/Components/Carousel";
import MultilineEllipsis from "@mcmc/XDL/Components/Common/MultiLineEllipsis";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export const enum fields {
    playlistUrl = "playlistUrl",
    buttonText = "buttonText",
    buttonUrl = "buttonUrl",
}

interface VideoPlayListWidgetProps extends WidgetProps {
    fields: {
        [fields.playlistUrl]: string;
        [fields.buttonText]: string;
        [fields.buttonUrl]: string;
    };
}

const mapStateToProps = (state: McMcApplicationState, ownProps: VideoPlayListWidgetProps) => ({
    playlistData: getYoutubePlaylistData(state, ownProps.id),
    isLoading: getMediaLoadingState(state),
    youTubeApiKey: getSettingsCollection(state).customSettings.youTubeApiKey,
});

const mapDispatchToProps = {
    loadYoutubePlaylistData,
};

type Props = VideoPlayListWidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface VideoPlayListWidgetStyles {
    videoContainer?: InjectableCss;
    videoTitle?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const videoPlayListWidgetStyles: VideoPlayListWidgetStyles = {
    videoContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            border: 1px solid ${getColor("common.ui3")};
            width: 255px;
            height: 100%;
            transition: border-color 0.2s;
            &:focus,
            &:hover {
                outline: none;
                border-color: ${getColor("text")};
            }
        `,
    },
    videoTitle: {
        variant: "h6",
        lineHeight: "20px",
        css: css`
            margin-top: 10px;
            text-align: center;
            height: 40px;
            padding: 0px 5px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 150px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

const styles = videoPlayListWidgetStyles;

const VideoPlayListWidget: React.FC<Props> = ({
    id,
    fields,
    loadYoutubePlaylistData,
    isLoading,
    playlistData,
    youTubeApiKey,
}) => {
    const destructListId = (url: string) => {
        const listRegex = /(\?|&)list=([^&#]+)/;
        const parts = url.match(listRegex);

        if (!parts) {
            // the list id already provided
            return url;
        }

        return parts[2];
    };

    React.useEffect(() => {
        if (fields.playlistUrl && youTubeApiKey) {
            const playlistId = destructListId(fields.playlistUrl);
            loadYoutubePlaylistData({
                playlistId,
                youTubeApiKey,
                key: id
            });
        }
    }, [fields.playlistUrl]);

    if (!playlistData) {
        return null;
    }

    return (
        <StyledWrapper>
            {createSectionTitleWidget(
                playlistData.title,
                { type: "Url", value: fields.buttonUrl },
                fields.buttonText,
                undefined,
                true,
            )}
            {isLoading && (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            )}
            {!isLoading && (
                <Carousel
                    id="youtube-playlist-videos"
                    maxNumberOfColumns={4}
                    slideGap={30}
                    slides={playlistData.videos.map(video => ({
                        id: video.id,
                        renderComponent: function VideoWrapper() {
                            return (
                                <StyledWrapper key={video.id} {...styles.videoContainer}>
                                    <Video src={video.id} />
                                    <MultilineEllipsis content={video.title} maxLines={2} {...styles.videoTitle} />
                                </StyledWrapper>
                            );
                        },
                    }))}
                />
            )}
        </StyledWrapper>
    );
};

export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            displayName: "Playlist URL",
            name: fields.playlistUrl,
            fieldType: "Contextual",
            editorTemplate: "TextField",
            defaultValue: "",
            isRequired: true,
        },
        {
            displayName: "Button Text",
            name: fields.buttonText,
            fieldType: "Contextual",
            editorTemplate: "TextField",
            defaultValue: "",
        },
        {
            displayName: "Button URL",
            name: fields.buttonUrl,
            fieldType: "Contextual",
            editorTemplate: "TextField",
            defaultValue: "",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(VideoPlayListWidget),
    definition,
};

export default widgetModule;
